import React, { useState, useEffect, useMemo } from 'react'
import { Select, Icon, Button, BUTTON_THEME } from '@st-operations/uikit/src/v2'
import { getApaUsers } from '_services/predactiv-admin'
import { SOURCE_VALUES, DROPDOWN_MENU_PROPS } from './constants'

const formatTimestamp = (ts) => {
  if (!ts) return ''
  const d = new Date(ts)
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const year = d.getFullYear()
  const hours = d.getHours()
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  const displayHours = String(hours % 12 || 12).padStart(2, '0')
  return `${month}.${day}.${year} - ${displayHours}:${minutes} ${ampm}`
}

const SORT_FIELD = {
  NAME: 'name',
  ORG: 'apaOrg',
  APPLICATION: 'application',
  UPDATED_AT: 'updatedAt',
}

const LoginTimestampTable = ({ orgOptions, source }) => {
  const [allData, setAllData] = useState([])
  // Single state object so org + user changes are atomic (avoids double-fetch)
  const [filters, setFilters] = useState({ org: undefined, user: undefined })
  const [search, setSearch] = useState('')
  const [sortField, setSortField] = useState(SORT_FIELD.UPDATED_AT)
  const [sortAsc, setSortAsc] = useState(false)

  const env = source?.split('-')?.[1]

  const applicationToShow = useMemo(() => {
    if (!source) return undefined
    if (source === SOURCE_VALUES.DATASPHERE) return 'DataSphere'
    if (source === SOURCE_VALUES.APA_PROD || source === SOURCE_VALUES.APA_DEV) return 'PDP'
    return undefined
  }, [source])

  useEffect(() => {
    getApaUsers(env, { org: filters.org?.value, email: filters.user })
      .then((res) => {
        const apaResponse = res?.users ?? res
        const responseList = apaResponse?.items || (Array.isArray(apaResponse) ? apaResponse : [])
        const users = responseList
          .filter((u) => u?.email)
          .map((u) => {
            const orgs = Array.isArray(u.organizations) ? u.organizations : []
            return {
              ...u,
              apaOrg: orgs
                .map((o) => o.name)
                .filter(Boolean)
                .join(', '),
              application: u.application || '',
              updatedAt: u.lastLogin || u.updatedAt,
            }
          })
        setAllData(users)
      })
      .catch(() => setAllData([]))
  }, [filters, env])

  const userOptions = useMemo(() => {
    const uniqueEmails = new Set()
    return allData
      .filter((u) => u.email && !uniqueEmails.has(u.email) && uniqueEmails.add(u.email))
      .map((u) => ({ label: u.email, value: u.email }))
      .sort((a, b) => (a.label > b.label ? 1 : -1))
  }, [allData])

  const filteredData = useMemo(() => {
    let result = allData
    if (applicationToShow) {
      result = result.filter((u) => u.application === applicationToShow)
    }
    const lowerSearch = search.toLowerCase()
    result = lowerSearch
      ? result.filter(
          (u) =>
            (u.name || '').toLowerCase().includes(lowerSearch) ||
            (u.email || '').toLowerCase().includes(lowerSearch) ||
            (u.apaOrg || '').toLowerCase().includes(lowerSearch) ||
            (u.application || '').toLowerCase().includes(lowerSearch) ||
            formatTimestamp(u.updatedAt).toLowerCase().includes(lowerSearch)
        )
      : result

    return [...result].sort((a, b) => {
      let aVal = a[sortField] ?? ''
      let bVal = b[sortField] ?? ''
      if (sortField === SORT_FIELD.UPDATED_AT) {
        aVal = aVal ? new Date(aVal).getTime() : 0
        bVal = bVal ? new Date(bVal).getTime() : 0
      } else {
        aVal = String(aVal).toLowerCase()
        bVal = String(bVal).toLowerCase()
      }
      if (aVal < bVal) return sortAsc ? -1 : 1
      if (aVal > bVal) return sortAsc ? 1 : -1
      return 0
    })
  }, [allData, search, sortField, sortAsc, applicationToShow])

  const handleSort = (field) => {
    if (sortField === field) {
      setSortAsc((prev) => !prev)
    } else {
      setSortField(field)
      setSortAsc(false)
    }
  }

  const handleExportCSV = () => {
    if (!filteredData.length) return
    const header = ['Email', 'Organization', 'Application', 'Login Timestamp']
    const rows = filteredData.map((u) => [
      JSON.stringify(u.email || ''),
      JSON.stringify(u.apaOrg || ''),
      JSON.stringify(u.application || ''),
      JSON.stringify(formatTimestamp(u.updatedAt)),
    ])
    const csv = [header.join(','), ...rows.map((r) => r.join(','))].join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.setAttribute('href', URL.createObjectURL(blob))
    link.setAttribute('download', `login-timestamps-${new Date().toISOString().split('T')[0]}.csv`)
    link.click()
  }

  const SortIndicator = ({ field }) => (
    <span className={`ml-1 ${sortField === field ? 'text-gray-800' : 'text-gray-400'}`}>
      {sortField === field && sortAsc ? '↑' : '↓'}
    </span>
  )

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row gap-4 flex-wrap items-end">
        <Select
          className="m-0 w-56"
          label="Filter By Organization"
          placeholder="Select organization"
          options={[{ label: 'All', value: undefined }, ...(orgOptions || [])]}
          value={(orgOptions || []).find((o) => o.value === filters.org?.value) || null}
          onChange={(option) =>
            setFilters({ org: option?.value ? option : undefined, user: undefined })
          }
          {...DROPDOWN_MENU_PROPS}
          useReactSelect
          isClearable
        />
        <Select
          className="m-0 w-56"
          label="Filter By User"
          placeholder="Select user"
          options={[{ label: 'All', value: undefined }, ...userOptions]}
          value={userOptions.find((o) => o.value === filters.user) || null}
          onChange={(option) =>
            setFilters((prev) => ({ ...prev, user: option?.value || undefined }))
          }
          {...DROPDOWN_MENU_PROPS}
          useReactSelect
          isClearable
        />
        <div className="ml-auto">
          <Button
            theme={BUTTON_THEME.PRIMARY}
            onClick={handleExportCSV}
            disabled={!filteredData.length}
          >
            <span className="inline-flex items-center gap-2">
              <Icon
                width="13px"
                height="13px"
                icon="download"
                type="outline"
                color="currentColor"
              />
              Export CSV
            </span>
          </Button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
          </span>
          <input
            type="text"
            placeholder="Name, Organization or Timestamp"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-[4px] text-sm focus:outline-none focus:ring-1 focus:ring-green-600"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-300">
              <th
                className="text-left py-2 pr-4 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                onClick={() => handleSort(SORT_FIELD.NAME)}
              >
                User Name <SortIndicator field={SORT_FIELD.NAME} />
              </th>
              <th
                className="text-left py-2 pr-4 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                onClick={() => handleSort(SORT_FIELD.ORG)}
              >
                Organization <SortIndicator field={SORT_FIELD.ORG} />
              </th>
              <th
                className="text-left py-2 pr-4 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap"
                onClick={() => handleSort(SORT_FIELD.APPLICATION)}
              >
                Application <SortIndicator field={SORT_FIELD.APPLICATION} />
              </th>
              <th
                className="text-left py-2 font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap border-b-2 border-green-700"
                onClick={() => handleSort(SORT_FIELD.UPDATED_AT)}
              >
                Login Timestamp <SortIndicator field={SORT_FIELD.UPDATED_AT} />
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan={4} className="py-6 text-center text-gray-400">
                  No data
                </td>
              </tr>
            ) : (
              filteredData.map((u, i) => (
                <tr key={u.email + i} className="border-b border-gray-200">
                  <td className="py-3 pr-4 text-gray-700">{u.name || u.email}</td>
                  <td className="py-3 pr-4 text-gray-700">{u.apaOrg}</td>
                  <td className="py-3 pr-4 text-gray-700">{u.application}</td>
                  <td className="py-3 text-gray-700">{formatTimestamp(u.updatedAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default LoginTimestampTable
